import { createStore } from 'redux'
import rootReducer from './reducers/rootReducer'
import logoutResetReducer from './reducers/logoutResetReducer'
import { enhancers } from './Enhancers'
import globalConfig from 'Helper/configHelpers'
import appConfig from './reducers/appConfigReducer'
import auth from './reducers/authReducer'
import interactableReducer, { scaleOffsetCompensation } from './reducers/interactableReducer'
import windowMessaging from './reducers/windowMessagingReducer'
import clock from './reducers/clockReducer'
import debug from 'Helper/debugHelpers'

import { useXtractStorage } from './useXtractStorage.js'
import { useCompressedLocalStorage } from './useCompressedLocalStorage.js'

useXtractStorage()

if (globalConfig.local_storage_compression === 'TRUE') {
  // A little shenanigans to avoid having npm tests accessing localStorage
  // since that causes problems (setup.js specifically hides localStorage
  // by assigning it to a mockStorage object).
  //
  // So, here we check to see if localStorage has been hidden, (meaning
  // that we are running in the test world) and if so, leave it alone.
  // Otherwise, use our compressed storage.

  if (!Object.keys(global.localStorage).includes('clear')) {
    // it has not been hidden, write over it with our compressing version
    useCompressedLocalStorage()
  }
}

const defaultAuthStore = {
  refreshInProgress: false, // Are we currently refreshing the token
  username: null,
  privilege: null, // What is the user level, Admin, User, LiteUser
  refreshToken: null,
  token: null, // local copy of the auth token
  API_FAIL_COUNT: 0, // How many times have we retried this api call
  refresherHandles: [] // task handles for token refresh in progress
}

const dashboardConfig = {
  screens: [],
  windows: []
}

const patientDisplayConfig = {
  screens: [],
  windows: [],
  workflows: []
}
// configuration settings both for the local system and from the db
// values below are defaults that get replaced with real values if
// they exist
export const defaultAppConfig = {
  CLOCK_TICK: 1,
  CLINIC_LOCATION: -1,
  users: [],
  dosingplans: [],
  treatmentplans: [],
  priority_names: [],
  providers: [],
  profiles: null,
  sizes: null,
  clinics: [],
  nurseDashboard: dashboardConfig,
  patientDisplay: patientDisplayConfig,
  connection: 'connected' // Indicator in upper right corner.
}

const lobbyDashboardConfig = {}

const defaultInteractableState = {
  // stores an array of window objects and their states
  windows: {},
  container: {
    width: 1024,
    height: 768
  }
}

const lobbyLogin = {
  lobbyPatient: null,
  questionnaire: null,
  patientAnswers: null,
  manualLogin: null
}

const lobbyLoginConfig = {}

// this will check for persisted auth in local storage and spread whatever
// we find into the defaultAuth store

const getStore = (reducerName, reducer, defaultStore = {}, type = 'local') => {
  const Storage = type === 'local' ? localStorage : sessionStorage
  const storedStore = Storage.getItem(reducerName)

  if (storedStore) {
    try {
      // if we successfully found and returned a stored auth object, use it
      const parsedStore = JSON.parse(storedStore)
      debug.log(
        'Rehydrating ' + reducerName + ' redux store',
        debug.logLevels['LOCAL.STORAGE']
      )
      return { ...defaultStore, ...parsedStore }
    } catch (e) {
      // else return the default structure
      return defaultStore
    }
  } else {
    return defaultStore
  }
}

const interactableState = getStore(
  'interactableState',
  interactableReducer,
  defaultInteractableState
)

// if there were windows stored in the open state, they were already scaled and offset but when they re-open
// they will be scaled and offset again so we need to "UN"scale and offset them before putting the values back
// into the store. We do this by using the previously stored container as the old container and a "newContainer"
// with no vertical or horizontal offset. This un-scales the window as if the offsets went away, then they will
// later be rescaled when the new correct offsets are determined
if (interactableState.windows && typeof interactableState.container.verticalOffset === 'number' && typeof interactableState.container.horizontalOffset === 'number') {
  const newContainer = { ...interactableState.container, verticalOffset: 0, horizontalOffset: 0 }
  Object.keys(interactableState.windows).forEach(ref => {
    interactableState.windows[ref] = scaleOffsetCompensation(interactableState.windows[ref], interactableState.container, newContainer)
  })
}
// create an object for the default data
export const defaultState = {
  interactableState,
  windowMessaging: getStore('windowMessaging', windowMessaging, {}),
  patientDisplay: { encounter: null },
  nurseDashboard: {},
  identity: null,
  adminDisplay: {},
  appConfig: getStore('appConfig', appConfig, defaultAppConfig),
  lobbyDashboardConfig,
  lobbyDashboard: {},
  lobbyLoginConfig,
  lobbyLogin,
  locks: { patient: [] },
  clock: getStore('clock', clock, { running: false }),
  streams: {},
  router: {
    location: { pathname: '' },
    match: { params: {}, url: '', query: {} }
  },
  modal: [],
  auth: getStore('auth', auth, defaultAuthStore),
  user: {},
  log: {
    patientId: null,
    username: null,
    prescriptionId: null,
    holding: []
  }
}

// Initialize the redux store
const Store = createStore(
  logoutResetReducer(rootReducer, defaultState),
  defaultState,
  enhancers
)

export default Store
